import type {
  ProvisionCanonicalComposition,
  ProvisionCompatibilitySelection,
  ProvisionCompositionDraft,
  ProvisionCompositionProfileSelection,
  ProvisionEquipmentCatalogueModel,
  ProvisionSoftwareCatalogueComponent,
} from '@/hooks/use-provision';

export type ManagedHardwareRoleState = {
  roleKey: string;
  displayName: string;
  requirement: 'required' | 'optional' | 'conditional';
  requirementReason: string | null;
  requiredNow: boolean;
  scope: 'endpoint' | 'application';
  sharing: 'exclusive' | 'same_host_when_supported';
  minimumCardinality: number;
  maximumCardinality: number;
  conditionEvidence: Array<{ code: string; message: string; matched: boolean }>;
  enabled: boolean;
  equipmentModelRevisionId: string | null;
  assetId: string | null;
  endpointPortId: string | null;
  peripheralBindingId: string | null;
  connectionProfileId: string | null;
  adapterComponentId: string | null;
  adapterComponentVersionId: string | null;
  adapterConfiguration: Record<string, unknown>;
  config: Record<string, unknown>;
  connectionParameters: Record<string, unknown>;
  secretReference: string;
  currentCandidateIds: Partial<
    Record<
      | 'equipment_model'
      | 'asset'
      | 'endpoint_port'
      | 'connection_method'
      | 'component'
      | 'component_version',
      string
    >
  >;
};

export type ManagedSoftwareState = {
  roleKey: string;
  componentId: string | null;
  componentVersionId: string | null;
  configuration: Record<string, unknown>;
  currentComponentId: string | null;
  currentComponentVersionId: string | null;
};

export type ManagedCompositionEditorState = {
  applicationId: string;
  applicationType: 'pos' | 'sco' | 'attendant';
  profileVersionId: string | null;
  sourceDraftVersion: number | null;
  sourceMode: 'managed' | 'manual' | null;
  convertManualDraft: boolean;
  hardware: Record<string, ManagedHardwareRoleState>;
  software: Record<string, ManagedSoftwareState>;
  acknowledgedWarningCodes: string[];
  dirty: boolean;
  lastInvalidation: string | null;
};

export type ManagedCompositionEditorAction =
  | { type: 'toggle-role'; roleKey: string; enabled: boolean }
  | {
      type: 'select-equipment';
      roleKey: string;
      id: string | null;
      configurationDefaults?: Record<string, unknown>;
    }
  | { type: 'select-asset'; roleKey: string; id: string | null }
  | {
      type: 'select-connection';
      roleKey: string;
      id: string | null;
      defaults?: Record<string, unknown>;
    }
  | { type: 'select-port'; roleKey: string; id: string | null }
  | { type: 'select-adapter-component'; roleKey: string; id: string | null }
  | {
      type: 'select-adapter-version';
      roleKey: string;
      id: string | null;
      defaults?: Record<string, unknown>;
    }
  | { type: 'set-adapter-config'; roleKey: string; key: string; value: unknown }
  | { type: 'set-role-config'; roleKey: string; key: string; value: unknown }
  | { type: 'set-connection-parameter'; roleKey: string; key: string; value: unknown }
  | { type: 'set-secret-reference'; roleKey: string; value: string }
  | { type: 'add-software'; roleKey: string }
  | { type: 'remove-software'; roleKey: string }
  | { type: 'select-software-component'; roleKey: string; id: string | null }
  | {
      type: 'select-software-version';
      roleKey: string;
      id: string | null;
      defaults?: Record<string, unknown>;
    }
  | { type: 'set-software-config'; roleKey: string; key: string; value: unknown }
  | { type: 'acknowledge-warning'; code: string; acknowledged: boolean }
  | { type: 'confirm-manual-conversion'; confirmed: boolean };

/**
 * Resolves a catalogue key, falling back to the baked English.
 *
 * The reducer and the draft builder are pure functions called outside React, so
 * `useT` — a hook — cannot be used here. The component passes its `t` in; every
 * other caller, including the specs that assert these sentences verbatim, gets
 * the English by default.
 */
export type CompositionTranslate = (key: string, fallback: string) => string;

const bakedEnglish: CompositionTranslate = (_key, fallback) => fallback;

export function managedCompositionEditorReducer(
  state: ManagedCompositionEditorState,
  action: ManagedCompositionEditorAction,
  translate: CompositionTranslate = bakedEnglish,
): ManagedCompositionEditorState {
  if (action.type === 'confirm-manual-conversion') {
    return { ...state, convertManualDraft: action.confirmed, dirty: true };
  }
  if (action.type === 'acknowledge-warning') {
    const values = new Set(state.acknowledgedWarningCodes);
    if (action.acknowledged) values.add(action.code);
    else values.delete(action.code);
    return { ...state, acknowledgedWarningCodes: Array.from(values).sort(), dirty: true };
  }
  if (action.type === 'add-software') {
    if (state.software[action.roleKey] !== undefined) return state;
    return {
      ...state,
      software: {
        ...state.software,
        [action.roleKey]: {
          roleKey: action.roleKey,
          componentId: null,
          componentVersionId: null,
          configuration: {},
          currentComponentId: null,
          currentComponentVersionId: null,
        },
      },
      dirty: true,
      lastInvalidation: translate(
        'provision.composition.notice.softwareAdded',
        'Added software role {role}; select a component and version.',
      ).replace('{role}', action.roleKey),
    };
  }
  if (action.type === 'remove-software') {
    const software = { ...state.software };
    delete software[action.roleKey];
    return {
      ...state,
      software,
      dirty: true,
      lastInvalidation: translate(
        'provision.composition.notice.softwareRemoved',
        'Removed software role {role}; dependency validation will rerun.',
      ).replace('{role}', action.roleKey),
    };
  }
  if (
    action.type === 'select-software-component' ||
    action.type === 'select-software-version' ||
    action.type === 'set-software-config'
  ) {
    const current = state.software[action.roleKey];
    if (current === undefined) return state;
    const next =
      action.type === 'select-software-component'
        ? {
            ...current,
            componentId: action.id,
            componentVersionId: null,
            configuration: {},
            currentComponentId: action.id,
            currentComponentVersionId: null,
          }
        : action.type === 'select-software-version'
          ? {
              ...current,
              componentVersionId: action.id,
              configuration: { ...(action.defaults ?? {}), ...current.configuration },
              currentComponentVersionId: action.id,
            }
          : {
              ...current,
              configuration: { ...current.configuration, [action.key]: action.value },
            };
    return {
      ...state,
      software: { ...state.software, [action.roleKey]: next },
      dirty: true,
      lastInvalidation:
        action.type === 'select-software-component'
          ? translate(
              'provision.composition.notice.softwareChanged',
              'Changed {role}; its version and downstream dependency plan were cleared.',
            ).replace('{role}', action.roleKey)
          : state.lastInvalidation,
    };
  }
  const role = state.hardware[action.roleKey];
  if (role === undefined) return state;
  let next = role;
  let message = state.lastInvalidation;
  switch (action.type) {
    case 'toggle-role':
      next = { ...role, enabled: action.enabled };
      message = translate(
        action.enabled
          ? 'provision.composition.notice.optionalRoleEnabled'
          : 'provision.composition.notice.optionalRoleRemoved',
        action.enabled ? 'Enabled optional role {role}.' : 'Removed optional role {role}.',
      ).replace('{role}', role.displayName);
      break;
    case 'select-equipment':
      next = {
        ...role,
        equipmentModelRevisionId: action.id,
        assetId: null,
        endpointPortId: null,
        connectionProfileId: null,
        adapterComponentId: null,
        adapterComponentVersionId: null,
        adapterConfiguration: {},
        config: { ...(action.configurationDefaults ?? {}), ...role.config },
        currentCandidateIds: {
          ...role.currentCandidateIds,
          equipment_model: action.id ?? undefined,
          asset: undefined,
          endpoint_port: undefined,
          connection_method: undefined,
          component: undefined,
          component_version: undefined,
        },
      };
      message = translate(
        'provision.composition.notice.modelChanged',
        'Changed {role} model; asset, connection, port, and adapter selections were cleared.',
      ).replace('{role}', role.displayName);
      break;
    case 'select-asset':
      next = {
        ...role,
        assetId: action.id,
        endpointPortId: null,
        adapterComponentId: null,
        adapterComponentVersionId: null,
        adapterConfiguration: {},
        currentCandidateIds: {
          ...role.currentCandidateIds,
          asset: action.id ?? undefined,
          endpoint_port: undefined,
          component: undefined,
          component_version: undefined,
        },
      };
      message = translate(
        'provision.composition.notice.assetChanged',
        'Changed {role} asset; port and adapter selections were cleared.',
      ).replace('{role}', role.displayName);
      break;
    case 'select-connection':
      next = {
        ...role,
        connectionProfileId: action.id,
        endpointPortId: null,
        adapterComponentId: null,
        adapterComponentVersionId: null,
        adapterConfiguration: {},
        connectionParameters: { ...(action.defaults ?? {}), ...role.connectionParameters },
        currentCandidateIds: {
          ...role.currentCandidateIds,
          connection_method: action.id ?? undefined,
          endpoint_port: undefined,
          component: undefined,
          component_version: undefined,
        },
      };
      message = translate(
        'provision.composition.notice.connectionChanged',
        'Changed {role} connection method; port and adapter selections were cleared.',
      ).replace('{role}', role.displayName);
      break;
    case 'select-port':
      next = {
        ...role,
        endpointPortId: action.id,
        adapterComponentId: null,
        adapterComponentVersionId: null,
        adapterConfiguration: {},
        currentCandidateIds: {
          ...role.currentCandidateIds,
          endpoint_port: action.id ?? undefined,
          component: undefined,
          component_version: undefined,
        },
      };
      message = translate(
        'provision.composition.notice.portChanged',
        'Changed {role} endpoint port; adapter compatibility must be selected again.',
      ).replace('{role}', role.displayName);
      break;
    case 'select-adapter-component':
      next = {
        ...role,
        adapterComponentId: action.id,
        adapterComponentVersionId: null,
        adapterConfiguration: {},
        currentCandidateIds: {
          ...role.currentCandidateIds,
          component: action.id ?? undefined,
          component_version: undefined,
        },
      };
      message = translate(
        'provision.composition.notice.adapterChanged',
        'Changed {role} adapter; select a compatible published version.',
      ).replace('{role}', role.displayName);
      break;
    case 'select-adapter-version':
      next = {
        ...role,
        adapterComponentVersionId: action.id,
        adapterConfiguration: { ...(action.defaults ?? {}), ...role.adapterConfiguration },
        currentCandidateIds: {
          ...role.currentCandidateIds,
          component_version: action.id ?? undefined,
        },
      };
      break;
    case 'set-adapter-config':
      next = {
        ...role,
        adapterConfiguration: { ...role.adapterConfiguration, [action.key]: action.value },
      };
      break;
    case 'set-role-config':
      next = { ...role, config: { ...role.config, [action.key]: action.value } };
      break;
    case 'set-connection-parameter':
      next = {
        ...role,
        connectionParameters: { ...role.connectionParameters, [action.key]: action.value },
      };
      break;
    case 'set-secret-reference':
      next = { ...role, secretReference: action.value };
      break;
  }
  return {
    ...state,
    hardware: { ...state.hardware, [action.roleKey]: next },
    dirty: true,
    lastInvalidation: message,
  };
}

export function createManagedCompositionEditor(params: {
  applicationId: string;
  applicationType: 'pos' | 'sco' | 'attendant';
  profile: ProvisionCompositionProfileSelection;
  draft: ProvisionCompositionDraft | null;
  desiredValues: Record<string, unknown>;
  softwareCatalogue: ProvisionSoftwareCatalogueComponent[];
}): ManagedCompositionEditorState {
  const draftComposition = params.draft?.composition;
  const desiredHardware = recordValue(params.desiredValues['hardware']);
  const desiredSoftware = recordValue(params.desiredValues['software']);
  const desiredComponents = recordValue(desiredSoftware['components']);
  const sourceHardware = draftComposition?.hardware ?? desiredHardware;
  const sourceComponents = draftComposition?.software.components ?? desiredComponents;
  const selections = params.draft?.selections ?? [];
  const validationRoles = new Map(
    params.profile.validation.roles.map((role) => [role.roleKey, role]),
  );
  const hardware = Object.fromEntries(
    (params.profile.selected?.roles ?? []).map((profileRole) => {
      const roleValidation = validationRoles.get(profileRole.roleKey);
      const raw = recordValue(sourceHardware[profileRole.roleKey]);
      const hasCurrent = Object.keys(raw).length > 0;
      const selection = selections.find((item) => item.roleKey === profileRole.roleKey);
      const equipmentId = optionalString(
        selection?.equipmentModelRevisionId ?? raw['equipmentModelRevisionId'],
      );
      const assetId = optionalString(selection?.assetId ?? raw['deviceId']);
      const portId = optionalString(selection?.endpointPortId ?? raw['endpointPortId']);
      const connectionId = optionalString(selection?.connectionProfileId);
      const adapterVersionId = optionalString(
        selection?.adapterComponentVersionId ?? raw['adapterComponentVersionId'],
      );
      const adapterComponentId = componentIdentityForVersion(
        params.softwareCatalogue,
        adapterVersionId,
      );
      const adapterConfiguration = componentConfigurationForVersion(
        sourceComponents,
        adapterVersionId,
      );
      const currentCandidateIds: ManagedHardwareRoleState['currentCandidateIds'] = {};
      currentCandidateIds.equipment_model =
        equipmentId ??
        (hasCurrent
          ? legacyCandidate('equipment', optionalString(raw['classKey']) ?? profileRole.roleKey)
          : undefined);
      currentCandidateIds.asset = assetId ?? undefined;
      currentCandidateIds.endpoint_port =
        portId ??
        (hasCurrent
          ? legacyCandidate(
              'port',
              `${optionalString(raw['connectionType']) ?? 'custom'}:${firstConnectionAddress(raw)}`,
            )
          : undefined);
      currentCandidateIds.connection_method =
        connectionId ??
        (hasCurrent
          ? legacyCandidate('connection', optionalString(raw['connectionType']) ?? 'custom')
          : undefined);
      currentCandidateIds.component =
        adapterComponentId ??
        (hasCurrent
          ? legacyCandidate('adapter', optionalString(raw['adapterId']) ?? 'custom')
          : undefined);
      currentCandidateIds.component_version =
        adapterVersionId ??
        (hasCurrent
          ? legacyCandidate(
              'adapter-version',
              `${optionalString(raw['adapterId']) ?? 'custom'}@${optionalString(raw['supportedVersion']) ?? 'unknown'}`,
            )
          : undefined);
      const state: ManagedHardwareRoleState = {
        roleKey: profileRole.roleKey,
        displayName: profileRole.displayName,
        requirement: profileRole.requirement,
        requirementReason: profileRole.requirementReason,
        requiredNow: roleValidation?.requiredNow ?? profileRole.requirement === 'required',
        scope: profileRole.scope,
        sharing: profileRole.sharing,
        minimumCardinality: roleValidation?.minimumCardinality ?? profileRole.minimumCardinality,
        maximumCardinality: profileRole.maximumCardinality,
        conditionEvidence: roleValidation?.conditionEvidence ?? [],
        enabled:
          (roleValidation?.requiredNow ?? profileRole.requirement === 'required') || hasCurrent,
        equipmentModelRevisionId: equipmentId,
        assetId,
        endpointPortId: portId,
        peripheralBindingId: optionalString(selection?.peripheralBindingId),
        connectionProfileId: connectionId,
        adapterComponentId,
        adapterComponentVersionId: adapterVersionId,
        adapterConfiguration,
        config: recordValue(raw['config']),
        connectionParameters: recordValue(raw['connectionParameters']),
        secretReference: optionalString(raw['secretReference']) ?? '',
        currentCandidateIds,
      };
      return [profileRole.roleKey, state];
    }),
  );
  const software = Object.fromEntries(
    Object.entries(sourceComponents).map(([roleKey, rawValue]) => {
      const raw = recordValue(rawValue);
      const componentVersionId = optionalString(raw['componentVersionId']);
      const currentIdentity =
        componentIdentityForVersion(params.softwareCatalogue, componentVersionId) ??
        componentIdentityByKey(params.softwareCatalogue, optionalString(raw['componentId']));
      return [
        roleKey,
        {
          roleKey,
          componentId: currentIdentity,
          componentVersionId,
          configuration: recordValue(raw['configuration']),
          currentComponentId:
            currentIdentity ??
            legacyCandidate('component', optionalString(raw['componentId']) ?? roleKey),
          currentComponentVersionId:
            componentVersionId ??
            legacyCandidate(
              'component-version',
              `${optionalString(raw['componentId']) ?? roleKey}@${optionalString(raw['version']) ?? 'unknown'}`,
            ),
        } satisfies ManagedSoftwareState,
      ];
    }),
  );
  return {
    applicationId: params.applicationId,
    applicationType: params.applicationType,
    profileVersionId: params.profile.selected?.versionId ?? null,
    sourceDraftVersion: params.draft?.version ?? null,
    sourceMode:
      params.draft?.authoringMode ??
      (Object.keys(sourceHardware).length > 0 || Object.keys(sourceComponents).length > 0
        ? 'manual'
        : null),
    convertManualDraft: false,
    hardware,
    software,
    acknowledgedWarningCodes: params.draft?.acknowledgedWarningCodes ?? [],
    dirty: false,
    lastInvalidation: null,
  };
}

export function compatibilitySelection(
  role: ManagedHardwareRoleState,
  selectedSoftwareVersionIds: string[],
): ProvisionCompatibilitySelection {
  return {
    roleKey: role.roleKey,
    equipmentModelRevisionId: role.equipmentModelRevisionId,
    assetId: role.assetId,
    endpointPortId: role.endpointPortId,
    peripheralBindingId: role.peripheralBindingId,
    connectionProfileId: role.connectionProfileId,
    adapterComponentVersionId: role.adapterComponentVersionId,
    componentVersionIds: Array.from(
      new Set([
        ...(role.adapterComponentVersionId === null ? [] : [role.adapterComponentVersionId]),
        ...selectedSoftwareVersionIds,
      ]),
    ).sort(),
    manualMode: false,
  };
}

export function buildManagedCompositionDraft(params: {
  state: ManagedCompositionEditorState;
  equipmentCatalogue: ProvisionEquipmentCatalogueModel[];
  softwareCatalogue: ProvisionSoftwareCatalogueComponent[];
  translate?: CompositionTranslate;
}): { draft: ReturnType<typeof managedDraft> | null; errors: string[] } {
  const errors: string[] = [];
  const { state } = params;
  const translate = params.translate ?? bakedEnglish;
  if (state.profileVersionId === null)
    errors.push(
      translate(
        'provision.composition.error.noProfile',
        'No applicable published composition profile.',
      ),
    );
  if (state.sourceMode === 'manual' && !state.convertManualDraft) {
    errors.push(
      translate(
        'provision.composition.error.confirmConversion',
        'Confirm conversion before replacing the saved Advanced/manual draft.',
      ),
    );
  }
  const hardware: ProvisionCanonicalComposition['hardware'] = {};
  const software: ProvisionCanonicalComposition['software']['components'] = {};
  const softwareVersions = Object.values(state.software).flatMap((component) =>
    component.componentVersionId === null ? [] : [component.componentVersionId],
  );
  const selections: ProvisionCompatibilitySelection[] = [];
  const projectedSoftwareVersions = new Set(
    Object.values(state.software).flatMap((component) =>
      component.componentVersionId === null ? [] : [component.componentVersionId],
    ),
  );
  const reservedSoftwareRoleKeys = new Set(Object.keys(state.software));
  for (const role of Object.values(state.hardware)) {
    if (!role.enabled) {
      if (role.requiredNow)
        errors.push(
          translate('provision.composition.error.roleRequired', '{role} is required.').replace(
            '{role}',
            role.displayName,
          ),
        );
      continue;
    }
    if (role.maximumCardinality !== 1) {
      errors.push(
        translate(
          'provision.composition.error.multiplicity',
          '{role} requires multiplicity {count}; this editor supports one binding per card.',
        )
          .replace('{role}', role.displayName)
          .replace('{count}', String(role.maximumCardinality)),
      );
    }
    const equipment = equipmentRevision(params.equipmentCatalogue, role.equipmentModelRevisionId);
    const connection = connectionProfile(params.equipmentCatalogue, role.connectionProfileId);
    const adapter = componentVersion(params.softwareCatalogue, role.adapterComponentVersionId);
    if (equipment === null)
      errors.push(
        translate(
          'provision.composition.error.selectEquipment',
          '{role}: select a supported equipment model.',
        ).replace('{role}', role.displayName),
      );
    if (role.assetId === null)
      errors.push(
        translate(
          'provision.composition.error.selectAsset',
          '{role}: select a physical asset.',
        ).replace('{role}', role.displayName),
      );
    if (connection === null)
      errors.push(
        translate(
          'provision.composition.error.selectConnection',
          '{role}: select a connection method.',
        ).replace('{role}', role.displayName),
      );
    if (role.adapterComponentId === null)
      errors.push(
        translate(
          'provision.composition.error.selectAdapter',
          '{role}: select an adapter.',
        ).replace('{role}', role.displayName),
      );
    if (adapter === null)
      errors.push(
        translate(
          'provision.composition.error.selectAdapterVersion',
          '{role}: select an adapter version.',
        ).replace('{role}', role.displayName),
      );
    if (equipment === null || role.assetId === null || connection === null || adapter === null) {
      continue;
    }
    hardware[role.roleKey] = {
      deviceId: role.assetId,
      classKey: equipment.classKeys[0] ?? role.roleKey,
      adapterId: adapter.component.key,
      supportedVersion: adapter.version.semanticVersion,
      connectionType: connection.connectionType,
      connectionParameters: role.connectionParameters as never,
      secretReference: role.secretReference || null,
      required: role.requiredNow,
      config: role.config as never,
      equipmentModelRevisionId: equipment.id,
      ...(role.endpointPortId === null ? {} : { endpointPortId: role.endpointPortId }),
      adapterComponentVersionId: adapter.version.componentVersionId,
    };
    if (!projectedSoftwareVersions.has(adapter.version.componentVersionId)) {
      const adapterRoleKey = uniqueSoftwareRoleKey(
        `${role.roleKey}_adapter`,
        software,
        reservedSoftwareRoleKeys,
      );
      software[adapterRoleKey] = {
        componentId: adapter.component.key,
        componentType: adapter.version.componentType,
        version: adapter.version.semanticVersion,
        componentVersionId: adapter.version.componentVersionId,
        artifactId: null,
        configuration: role.adapterConfiguration as never,
      };
      projectedSoftwareVersions.add(adapter.version.componentVersionId);
      reservedSoftwareRoleKeys.add(adapterRoleKey);
    }
    selections.push(compatibilitySelection(role, softwareVersions));
  }
  for (const component of Object.values(state.software)) {
    const identity = params.softwareCatalogue.find(
      (candidate) => candidate.componentId === component.componentId,
    );
    const version = identity?.versions.find(
      (candidate) => candidate.componentVersionId === component.componentVersionId,
    );
    if (identity === undefined) {
      errors.push(
        translate(
          'provision.composition.error.selectSoftware',
          '{role}: select a software component.',
        ).replace('{role}', component.roleKey),
      );
      continue;
    }
    if (version === undefined) {
      errors.push(
        translate(
          'provision.composition.error.selectSoftwareVersion',
          '{role}: select a published compatible version.',
        ).replace('{role}', component.roleKey),
      );
      continue;
    }
    software[component.roleKey] = {
      componentId: identity.key,
      componentType: version.componentType,
      version: version.semanticVersion,
      componentVersionId: version.componentVersionId,
      artifactId: null,
      configuration: component.configuration as never,
    };
  }
  const allVersions = Array.from(
    new Set([
      ...softwareVersions,
      ...Object.values(state.hardware).flatMap((role) =>
        role.enabled && role.adapterComponentVersionId !== null
          ? [role.adapterComponentVersionId]
          : [],
      ),
    ]),
  ).sort();
  selections.push({
    roleKey: null,
    equipmentModelRevisionId: null,
    assetId: null,
    endpointPortId: null,
    peripheralBindingId: null,
    connectionProfileId: null,
    adapterComponentVersionId: null,
    componentVersionIds: allVersions,
    manualMode: false,
  });
  if (errors.length > 0 || state.profileVersionId === null) return { draft: null, errors };
  return {
    draft: managedDraft({
      state,
      composition: {
        schemaVersion: 1,
        applicationInstanceId: state.applicationId,
        applicationType: state.applicationType,
        profileVersionId: state.profileVersionId,
        hardware,
        software: { components: software },
      },
      selections,
    }),
    errors,
  };
}

function managedDraft(params: {
  state: ManagedCompositionEditorState;
  composition: ProvisionCanonicalComposition;
  selections: ProvisionCompatibilitySelection[];
}) {
  return {
    schemaVersion: 1 as const,
    expectedVersion: params.state.sourceDraftVersion,
    authoringMode: 'managed' as const,
    composition: params.composition,
    selections: params.selections,
    acknowledgedWarningCodes: params.state.acknowledgedWarningCodes,
  };
}

function componentConfigurationForVersion(
  components: Record<string, unknown>,
  componentVersionId: string | null,
) {
  if (componentVersionId === null) return {};
  for (const value of Object.values(components)) {
    const component = recordValue(value);
    if (component['componentVersionId'] === componentVersionId) {
      return recordValue(component['configuration']);
    }
  }
  return {};
}

function uniqueSoftwareRoleKey(
  preferred: string,
  software: ProvisionCanonicalComposition['software']['components'],
  reserved: Set<string>,
) {
  if (software[preferred] === undefined && !reserved.has(preferred)) return preferred;
  let suffix = 2;
  while (
    software[`${preferred}_${suffix}`] !== undefined ||
    reserved.has(`${preferred}_${suffix}`)
  ) {
    suffix += 1;
  }
  return `${preferred}_${suffix}`;
}

export function equipmentRevision(
  catalogue: ProvisionEquipmentCatalogueModel[],
  id: string | null,
) {
  if (id === null) return null;
  for (const model of catalogue) {
    const revision = model.revisions.find((candidate) => candidate.id === id);
    if (revision !== undefined) return revision;
  }
  return null;
}

export function connectionProfile(
  catalogue: ProvisionEquipmentCatalogueModel[],
  id: string | null,
) {
  if (id === null) return null;
  for (const model of catalogue) {
    for (const revision of model.revisions) {
      const connection = revision.connectionProfiles.find((candidate) => candidate.id === id);
      if (connection !== undefined) return connection;
    }
  }
  return null;
}

export function componentVersion(
  catalogue: ProvisionSoftwareCatalogueComponent[],
  id: string | null,
) {
  if (id === null) return null;
  for (const component of catalogue) {
    const version = component.versions.find((candidate) => candidate.componentVersionId === id);
    if (version !== undefined) return { component, version };
  }
  return null;
}

function componentIdentityForVersion(
  catalogue: ProvisionSoftwareCatalogueComponent[],
  versionId: string | null,
) {
  return componentVersion(catalogue, versionId)?.component.componentId ?? null;
}

function componentIdentityByKey(
  catalogue: ProvisionSoftwareCatalogueComponent[],
  key: string | null,
) {
  if (key === null) return null;
  return catalogue.find((component) => component.key === key)?.componentId ?? null;
}

function legacyCandidate(kind: string, value: string) {
  return `custom-legacy:${kind}:${value}`.slice(0, 160);
}

function firstConnectionAddress(raw: Record<string, unknown>) {
  const parameters = recordValue(raw['connectionParameters']);
  return (
    optionalString(parameters['port'] ?? parameters['address'] ?? parameters['socket']) ?? 'custom'
  );
}

function recordValue(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function optionalString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}
